import { FACTOR_BY_KEY } from "./graphModel";

const GraphView = ({
  svgRef,
  sim,
  graph,
  games,
  sel,
  cmp,
  hov,
  cluster,
  path,
  panning,
  labels = "major",
  nodeShape = "square",
  onNodeDown,
  onNodeEnter,
  onNodeLeave,
  onBgDown,
  onMove,
  onUp,
}) => {
  const { pos: P, view: v, size } = sim;
  const { w, h } = size;
  const k = v.k;
  const { edges, radius, adj, clusters, topList } = graph;
  const square = nodeShape === "square";
  const topSet = new Set(
    topList.slice(0, Math.max(6, Math.min(24, Math.round((w * h * k * k) / 26000))))
  );

  // Decide which nodes/edges are highlighted: hover or selection first,
  // then a comparison path, then a cluster.
  const center = hov ?? (cmp == null ? sel : null);
  let focus = null;
  let isHot = null;
  if (center != null) {
    focus = new Set([center]);
    adj[center].forEach((nb) => focus.add(nb.o));
    isHot = (e) => e.a === center || e.b === center;
  } else if (cmp != null && sel != null) {
    const p = path || [sel, cmp];
    focus = new Set(p);
    const pathEdges = new Set();
    for (let i = 0; i < p.length - 1; i++) {
      pathEdges.add(`${Math.min(p[i], p[i + 1])}-${Math.max(p[i], p[i + 1])}`);
    }
    isHot = (e) => pathEdges.has(`${e.a}-${e.b}`);
  } else if (cluster != null && clusters[cluster]) {
    focus = new Set(clusters[cluster].members);
    isHot = (e) => focus.has(e.a) && focus.has(e.b);
  }

  const lines = edges.map((e) => {
    const hot = isHot && isHot(e);
    const A = P[e.a];
    const B = P[e.b];
    return (
      <line
        key={`${e.a}-${e.b}`}
        x1={A.x}
        y1={A.y}
        x2={B.x}
        y2={B.y}
        vectorEffect="non-scaling-stroke"
        style={{
          stroke: hot ? "var(--color-accent)" : "var(--color-text)",
          strokeWidth: hot ? 1.2 + e.s * 3.6 : 0.6 + e.s * 2.6,
          opacity: focus ? (hot ? 0.95 : 0.05) : 0.12 + e.s * 0.5,
          strokeDasharray: FACTOR_BY_KEY[e.dom].dash,
        }}
      />
    );
  });

  const nodes = [];
  const labelEls = [];
  games.forEach((g, i) => {
    const R = radius[i];
    const fade = focus && !focus.has(i);
    const active = i === sel || i === cmp;
    const hovered = i === hov;
    const fill = active
      ? "var(--color-accent)"
      : fade
      ? "var(--color-neutral-300)"
      : "var(--color-text)";
    const shapeStyle = { fill, stroke: "var(--color-bg)", strokeWidth: 1.5 };
    const ringStyle = {
      fill: "none",
      stroke: active ? "var(--color-accent)" : "var(--color-text)",
      strokeWidth: 2,
    };
    const ringPad = 4 / k;
    nodes.push(
      <g
        key={g.id}
        transform={`translate(${P[i].x},${P[i].y})`}
        style={{ cursor: "pointer" }}
        onPointerDown={(ev) => onNodeDown(i, ev)}
        onPointerEnter={() => onNodeEnter(i)}
        onPointerLeave={onNodeLeave}
      >
        <rect
          x={-R - 6 / k}
          y={-R - 6 / k}
          width={R * 2 + 12 / k}
          height={R * 2 + 12 / k}
          style={{ fill: "transparent" }}
        />
        {square ? (
          <rect x={-R} y={-R} width={R * 2} height={R * 2} style={shapeStyle} vectorEffect="non-scaling-stroke" />
        ) : (
          <circle r={R} style={shapeStyle} vectorEffect="non-scaling-stroke" />
        )}
        {(active || hovered) &&
          (square ? (
            <rect
              x={-R - ringPad}
              y={-R - ringPad}
              width={R * 2 + ringPad * 2}
              height={R * 2 + ringPad * 2}
              vectorEffect="non-scaling-stroke"
              style={ringStyle}
            />
          ) : (
            <circle r={R + ringPad} vectorEffect="non-scaling-stroke" style={ringStyle} />
          ))}
      </g>
    );
    const show =
      labels === "all" ||
      hovered ||
      active ||
      (focus ? focus.has(i) && focus.size <= 18 : labels === "major" && topSet.has(i));
    if (show) {
      labelEls.push(
        <text
          key={`t-${g.id}`}
          x={P[i].x + R + 5 / k}
          y={P[i].y}
          dy="0.35em"
          style={{
            fontSize: (active ? 13.5 : 11.5) / k,
            fontWeight: active ? 800 : 600,
            fill: fade
              ? "var(--color-neutral-400)"
              : active
              ? "var(--color-accent-700)"
              : "var(--color-text)",
            paintOrder: "stroke",
            stroke: "var(--color-bg)",
            strokeWidth: 4 / k,
            strokeLinejoin: "round",
            pointerEvents: "none",
            fontFamily: "var(--font-body)",
          }}
        >
          {g.name}
        </text>
      );
    }
  });

  return (
    <svg
      ref={svgRef}
      width={w}
      height={h}
      viewBox={`${-w / 2 / k - v.x} ${-h / 2 / k - v.y} ${w / k} ${h / k}`}
      className="ludograph-svg"
      style={{ cursor: panning ? "grabbing" : "default" }}
      onPointerDown={onBgDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onUp}
    >
      <g>{lines}</g>
      <g>{nodes}</g>
      <g>{labelEls}</g>
    </svg>
  );
};

export default GraphView;
